import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
} from '@mui/material';
import {
  Pencil as EditIcon,
  Search as SearchIcon,
  Plus as AddIcon,
  ListPlus as SeriesIcon,
  ClipboardList as BacklogIcon,
  ArrowRight as ArrowIcon,
  User as PersonIcon,
} from 'lucide-react';
import { HEAD_CELL_SX, ROW_CELL_SX, TABLE_CONTAINER_SX } from '../../styles/tableStyles';
import api from '../../utils/axios';
import { getMissingNotes, listNotes, listAppointments } from '../../utils/notesApi';
import PatientFormDialog from '../../components/PatientFormDialog';

// Derive a display MRN — uses the stored value when present, otherwise a
// deterministic synthetic "MRN-XXXXXX" from the patient id (matches
// PatientsManagement.js convention).
function deriveMrn(p) {
  if (p.mrn) return p.mrn;
  return 'MRN-' + (p.patient_id || p.id || '').replaceAll('-', '').slice(0, 6).toUpperCase();
}

// ---------------------------------------------------------------------------
// Start New Series dialog — picks or creates a patient, then navigates
// ---------------------------------------------------------------------------

const StartSeriesDialog = ({ open, patients, onNewPatient, onClose }) => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const [createPatientOpen, setCreatePatientOpen] = useState(false);

  const handleStart = () => {
    if (!selected) return;
    navigate(`/notes/patients/${selected.patient_id}?startSeries=true`);
    onClose();
  };

  const handlePatientCreated = (newPatient) => {
    onNewPatient(newPatient);   // add to parent list so it appears in table
    setSelected(newPatient);    // auto-select the newly created patient
    setCreatePatientOpen(false);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SeriesIcon size={16} />
          Start New Note Series
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select the patient you want to start documenting for. You'll then add
            their appointment dates and the system will generate notes in order.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Autocomplete
              sx={{ flex: 1 }}
              options={patients}
              getOptionLabel={(p) => `${p.first_name} ${p.last_name}`}
              isOptionEqualToValue={(option, value) => option.patient_id === value.patient_id}
              value={selected}
              onChange={(_, v) => setSelected(v)}
              openOnFocus
              disablePortal
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Patient"
                  placeholder="Search by name…"
                  autoFocus
                />
              )}
              renderOption={(props, p) => (
                <li {...props} key={p.patient_id}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {p.first_name} {p.last_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      DOB: {p.date_of_birth}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      MRN: {deriveMrn(p)}
                    </Typography>
                  </Box>
                </li>
              )}
            />
            <Button
              variant="outlined"
              startIcon={<AddIcon size={16} />}
              onClick={() => setCreatePatientOpen(true)}
              sx={{ mt: 0.5, whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              New Patient
            </Button>
          </Box>
          {selected && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Selected: {selected.first_name} {selected.last_name} — DOB: {selected.date_of_birth}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleStart}
            disabled={!selected}
            endIcon={<ArrowIcon size={16} />}
          >
            Set Up Appointments
          </Button>
        </DialogActions>
      </Dialog>

      <PatientFormDialog
        open={createPatientOpen}
        onClose={() => setCreatePatientOpen(false)}
        onPatientCreated={handlePatientCreated}
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// Summary card (hero section)
// ---------------------------------------------------------------------------

const SummaryCard = ({ icon, title, subtitle, action, onClick, color = 'primary' }) => (
  <Paper
    elevation={2}
    sx={{
      p: 3,
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      cursor: onClick ? 'pointer' : 'default',
      borderTop: '3px solid',
      borderColor: `${color}.main`,
      '&:hover': onClick ? { bgcolor: 'action.hover' } : {},
    }}
    onClick={onClick}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: `${color}.main` }}>
      {icon}
      <Typography variant="subtitle1" fontWeight={600}>
        {title}
      </Typography>
    </Box>
    <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
      {subtitle}
    </Typography>
    {action}
  </Paper>
);

// ---------------------------------------------------------------------------
// Status chip for patient row
// ---------------------------------------------------------------------------

const SeriesStatusChip = ({ apptCount, missingCount, draftCount }) => {
  if (apptCount === 0) return <Chip label="No series" size="small" variant="outlined" color="default" />;
  if (missingCount > 0) return <Chip label={`${missingCount} missing`} size="small" color="warning" />;
  if (draftCount > 0) return <Chip label={`${draftCount} drafts`} size="small" color="info" />;
  return <Chip label="Up to date" size="small" color="success" />;
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const NotesBacklogPage = () => {
  const [patients, setPatients] = useState([]);
  const [statsMap, setStatsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [startSeriesOpen, setStartSeriesOpen] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const patientsRes = await api.get('/patients/');
      const raw = patientsRes.data;
      const allPatients = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.data)
        ? raw.data
        : Array.isArray(raw?.results)
        ? raw.results
        : [];
      setPatients(allPatients);

      const map = {};
      await Promise.all(
        allPatients.map(async (p) => {
          try {
            const [apptRes, missingRes, notesRes] = await Promise.all([
              listAppointments(p.patient_id),
              getMissingNotes(p.patient_id),
              listNotes(p.patient_id),
            ]);
            const appts = Array.isArray(apptRes.data) ? apptRes.data : (apptRes.data?.results ?? []);
            const missing = Array.isArray(missingRes.data) ? missingRes.data : (missingRes.data?.results ?? []);
            const notes = Array.isArray(notesRes.data) ? notesRes.data : (notesRes.data?.results ?? []);
            map[p.patient_id] = {
              apptCount: appts.length,
              missingCount: missing.length,
              draftCount: notes.filter((n) => n.status === 'draft').length,
              approvedCount: notes.filter((n) => n.status === 'approved').length,
            };
          } catch {
            map[p.patient_id] = { apptCount: 0, missingCount: 0, draftCount: 0, approvedCount: 0 };
          }
        })
      );
      setStatsMap(map);
    } catch {
      setError('Failed to load patients.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const totalMissing = Object.values(statsMap).reduce((s, b) => s + b.missingCount, 0);
  const totalDrafts = Object.values(statsMap).reduce((s, b) => s + b.draftCount, 0);
  const totalSeries = Object.values(statsMap).filter((b) => b.apptCount > 0).length;

  const filtered = patients.filter((p) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      p.first_name.toLowerCase().includes(q) ||
      p.last_name.toLowerCase().includes(q) ||
      deriveMrn(p).toLowerCase().includes(q)
    );
  });

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            PT Notes
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Generate, review, and manage clinical notes for your patients.
          </Typography>
        </Box>
        <Button
          component={Link}
          to="/notes/style-template"
          variant="outlined"
          startIcon={<EditIcon size={16} />}
          size="small"
        >
          My Style Template
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Hero cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
        <SummaryCard
          color="primary"
          icon={<SeriesIcon size={16} />}
          title="Start New Note Series"
          subtitle="Pick a patient, enter their appointment dates, and generate a full set of PT notes in order."
          action={
            <Button
              variant="contained"
              startIcon={<AddIcon size={16} />}
              onClick={() => setStartSeriesOpen(true)}
              sx={{ mt: 1, alignSelf: 'flex-start' }}
            >
              Start Series
            </Button>
          }
        />

        <SummaryCard
          color={totalMissing > 0 ? 'warning' : 'success'}
          icon={<BacklogIcon size={16} />}
          title="Note Backlog"
          subtitle={
            loading
              ? 'Loading…'
              : totalMissing > 0 || totalDrafts > 0
              ? `${totalMissing} missing note${totalMissing !== 1 ? 's' : ''} · ${totalDrafts} draft${totalDrafts !== 1 ? 's' : ''} awaiting review`
              : 'All notes are up to date.'
          }
          action={
            !loading && (totalMissing > 0 || totalDrafts > 0) ? (
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                {totalMissing > 0 && (
                  <Chip label={`${totalMissing} missing`} color="warning" size="small" />
                )}
                {totalDrafts > 0 && (
                  <Chip label={`${totalDrafts} to review`} color="info" size="small" />
                )}
              </Box>
            ) : null
          }
        />

        <SummaryCard
          color="info"
          icon={<PersonIcon size={16} />}
          title="Active Series"
          subtitle={
            loading
              ? 'Loading…'
              : `${totalSeries} patient${totalSeries !== 1 ? 's' : ''} with appointment series set up.`
          }
          action={
            !loading && totalSeries === 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Use "Start New Series" to set up your first patient.
              </Typography>
            ) : null
          }
        />
      </Box>

      {/* Patient table */}
      <Paper elevation={1}>
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle2" color="text.secondary">
            All Patients
          </Typography>
          <TextField
            size="small"
            placeholder="Search patients…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon size={16} />
                </InputAdornment>
              ),
            }}
            sx={{ width: 220 }}
          />
        </Box>

        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {search ? 'No patients match that search.' : 'No patients found.'}
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={TABLE_CONTAINER_SX}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={HEAD_CELL_SX}>Patient</TableCell>
                  <TableCell sx={{ ...HEAD_CELL_SX, textAlign: 'center' }}>Appointments</TableCell>
                  <TableCell sx={{ ...HEAD_CELL_SX, textAlign: 'center' }}>Approved Notes</TableCell>
                  <TableCell sx={{ ...HEAD_CELL_SX, textAlign: 'center' }}>Status</TableCell>
                  <TableCell sx={{ ...HEAD_CELL_SX, textAlign: 'right' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((p) => {
                  const stats = statsMap[p.patient_id];
                  const loaded = !!stats;
                  return (
                    <TableRow
                      key={p.patient_id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      component={Link}
                      to={`/notes/patients/${p.patient_id}`}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {p.first_name} {p.last_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            DOB: {p.date_of_birth}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            MRN: {deriveMrn(p)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        {loaded ? (
                          <Typography variant="body2">{stats.apptCount}</Typography>
                        ) : (
                          <CircularProgress size={12} />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {loaded ? (
                          <Typography variant="body2">{stats.approvedCount}</Typography>
                        ) : (
                          <CircularProgress size={12} />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {loaded ? (
                          <SeriesStatusChip
                            apptCount={stats.apptCount}
                            missingCount={stats.missingCount}
                            draftCount={stats.draftCount}
                          />
                        ) : (
                          <CircularProgress size={12} />
                        )}
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        {loaded && stats.apptCount === 0 ? (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddIcon size={16} />}
                            component={Link}
                            to={`/notes/patients/${p.patient_id}?startSeries=true`}
                          >
                            Start Series
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="text"
                            endIcon={<ArrowIcon size={16} />}
                            component={Link}
                            to={`/notes/patients/${p.patient_id}`}
                          >
                            Open
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <StartSeriesDialog
        open={startSeriesOpen}
        patients={patients}
        onNewPatient={(p) => setPatients((prev) => [...prev, p])}
        onClose={() => setStartSeriesOpen(false)}
      />
    </Container>
  );
};

export default NotesBacklogPage;
